import dayjs from 'dayjs';
import type { StatusTone } from '@vnetwork/ui/data';
import { env } from '@/config/env';
import type { Channel, Schedule, Storage } from '@/api/fastchannel';
import { playoutModeLabel } from './playout-modes';

// ---------------------------------------------------------------------------
// URLs
// ---------------------------------------------------------------------------

/** Playable HLS URL for a channel. The @parrot packager serves the linear
 *  output at /channel/{id}/media.m3u8 keyed by the channel's UUID. */
export function channelM3u8Url(channelId: string | undefined): string {
  if (!channelId) return '';
  return `${env.fastChannelUrl}/channel/${channelId}/media.m3u8`;
}

/**
 * Rewrite a bare `.mp4` source into the on-the-fly HLS URLset the playout engine
 * serves (`file.mp4` → `file,.mp4.urlset/master.m3u8`). Ported from the legacy
 * `handleOnTheFly`.
 */
export function onTheFly(url: string): string {
  const ext = url.match(/\.mp4$/)?.[0];
  if (!ext) return url;
  return url.replace(ext, `,${ext}.urlset/master.m3u8`);
}

/** Build a playable source URL for a storage file key. */
export function buildSourceUrl(storage: Storage, key: string): string {
  const base = `${storage.public_url ?? ''}/${key}`;
  if (key.match(/\.m3u8$/)) return base;
  if (key.match(/\.mp4$/)) return onTheFly(base);
  return base;
}

/** Storage browser only offers playable media. */
export function isMediaFile(key: string): boolean {
  return /\.(mp4|m3u8)$/.test(key);
}

// ---------------------------------------------------------------------------
// Channel display
// ---------------------------------------------------------------------------

export function playModeLabel(channel: Pick<Channel, 'repeat' | 'repeat_mode'>): string {
  return playoutModeLabel(channel);
}

/** Channel active/inactive → StatusPill tone + label. */
export function channelStatusTone(status: string | undefined): StatusTone {
  return status === 'inactive' ? 'muted' : 'success';
}
export function channelStatusLabel(status: string | undefined): string {
  return status === 'inactive' ? 'Inactive' : 'Active';
}
export function isChannelDisabled(status: string | undefined): boolean {
  return status === 'inactive';
}

// ---------------------------------------------------------------------------
// Content-library asset status (ingest normalization: pending → normalizing →
// ready | failed). An asset is playable only when `ready`; until then its url
// is empty, so pickers must gate on readiness.
// ---------------------------------------------------------------------------

export function assetStatusTone(status: string | undefined): StatusTone {
  switch (status) {
    case 'ready':
      return 'success';
    case 'pending':
    case 'normalizing':
      return 'info';
    case 'failed':
      return 'danger';
    default:
      return 'muted';
  }
}

export function assetStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'normalizing':
      return 'Processing';
    case 'pending':
      return 'Queued';
    case 'failed':
      return 'Failed';
    default:
      return status || 'Unknown';
  }
}

/** Human label for a normalization sub-step (stage). */
export function assetStageLabel(stage: string | undefined): string {
  switch (stage) {
    case 'downloading':
      return 'Downloading';
    case 'encoding':
      return 'Encoding';
    case 'uploading':
      return 'Uploading';
    default:
      return 'Processing';
  }
}

/** Status-pill text while normalizing: the stage, plus the encode percent when
 *  available (e.g. "Encoding 45%"). Falls back to the plain status label. */
export function assetProcessingLabel(
  status: string | undefined,
  stage: string | undefined,
  progress: number | undefined,
): string {
  if (status !== 'normalizing') return assetStatusLabel(status);
  const base = assetStageLabel(stage);
  if (stage === 'encoding' && typeof progress === 'number' && progress > 0) {
    return `${base} ${progress}%`;
  }
  return base;
}

/** Format a millisecond duration compactly: 250ms, 1.2s, 1m 5s. */
export function formatMs(ms: number | undefined): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

/** True only when the asset is normalized and has a playable url. */
export function isAssetReady(status: string | undefined): boolean {
  return status === 'ready';
}

/** True while the asset is still being ingested/normalized. */
export function isAssetProcessing(status: string | undefined): boolean {
  return status === 'pending' || status === 'normalizing';
}

// ---------------------------------------------------------------------------
// Schedule display
// ---------------------------------------------------------------------------

/** The program actually on air right now + how far into it. Mirrors the
 *  packager's time-addressed playout (Anchor = earliest start, period = Σ slots,
 *  pos = (now − Anchor) mod period) so the FE shows exactly what the packager
 *  plays — including looped/repeat channels, where naive absolute-time matching
 *  would wrongly show everything as expired. */
export interface LiveProgram {
  program: Schedule;
  /** Index in the (filtered, sorted) program list. */
  index: number;
  /** Milliseconds into the current program. */
  offsetMs: number;
  /** Wall-clock ms when the current slot ends. */
  endsAtMs: number;
}

// shuffleKey MUST stay byte-for-byte in sync with the packager's shuffleKey
// (FNV-1a 32-bit over "<cycle>:<id>") so the console highlights the same on-air
// program a shuffle channel is actually playing. cycle must be an integer; ids
// are ASCII (uuid/slug) so charCodeAt == the byte the Go side hashes.
export function shuffleKey(cycle: number, id: string): number {
  let h = 0x811c9dc5; // FNV-1a 32 offset basis
  const s = `${cycle}:${id}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0; // FNV prime, keep uint32
  }
  return h >>> 0;
}

export function liveProgram(
  schedules: Schedule[],
  repeat: boolean,
  now = Date.now(),
  mode?: string,
): LiveProgram | null {
  // Same filter the packager applies in mapEPG: skip zero-length / source-less.
  const progs = schedules
    .filter((s) => s.end_time > s.start_time && !!s.url)
    .sort((a, b) => a.start_time - b.start_time);
  if (progs.length === 0) return null;

  if (!repeat) {
    // Play-once: the program whose absolute window contains now (else nothing).
    const idx = progs.findIndex((s) => s.start_time <= now && now < s.end_time);
    if (idx < 0) return null;
    return { program: progs[idx], index: idx, offsetMs: now - progs[idx].start_time, endsAtMs: progs[idx].end_time };
  }

  // Repeat: loop the lineup with period = Σ slot, anchored at the first start.
  const anchor = progs[0].start_time;
  const slots = progs.map((s) => s.end_time - s.start_time);
  const period = slots.reduce((a, b) => a + b, 0);
  if (period <= 0) return null;
  const rel = Math.max(0, now - anchor);
  const pos = rel % period;

  // shuffle: permute the lineup each cycle, mirroring the packager's
  // shuffledOrder, so now-playing is correct on shuffle channels.
  let order = progs.map((_, i) => i);
  if (mode === 'shuffle' && progs.length > 1) {
    const cycle = Math.floor(rel / period);
    order = order.sort((a, b) => {
      const ha = shuffleKey(cycle, progs[a].id);
      const hb = shuffleKey(cycle, progs[b].id);
      if (ha !== hb) return ha - hb;
      return progs[a].id < progs[b].id ? -1 : progs[a].id > progs[b].id ? 1 : 0;
    });
  }

  let acc = 0;
  for (const i of order) {
    if (pos < acc + slots[i]) {
      const offsetMs = pos - acc;
      return { program: progs[i], index: i, offsetMs, endsAtMs: now + (slots[i] - offsetMs) };
    }
    acc += slots[i];
  }
  const lastI = order[order.length - 1];
  return { program: progs[lastI], index: lastI, offsetMs: 0, endsAtMs: now + slots[lastI] };
}

export type ScheduleStatus = 'playing' | 'scheduled' | 'past' | 'expired';

/** Classify a schedule against the current wall-clock time (ms). */
export function scheduleStatus(s: Pick<Schedule, 'start_time' | 'end_time'>, now = Date.now()): ScheduleStatus {
  if (s.end_time < now) return 'expired';
  if (s.start_time <= now && now < s.end_time) return 'playing';
  if (s.start_time > now) return 'scheduled';
  return 'past';
}

export const SCHEDULE_STATUS_TONE: Record<ScheduleStatus, StatusTone> = {
  playing: 'success',
  scheduled: 'info',
  past: 'muted',
  expired: 'muted',
};

export const SCHEDULE_STATUS_LABEL: Record<ScheduleStatus, string> = {
  playing: 'On air',
  scheduled: 'Upcoming',
  past: 'Past',
  expired: 'Expired',
};

/** Accent colour token per program type (vod/live/ads). */
export function scheduleTypeColor(type: string | undefined): string {
  switch (type) {
    case 'live':
      return 'var(--vn-danger-solid)';
    case 'ads':
      return 'var(--vn-info-solid)';
    case 'vod':
      return 'var(--vn-warning-solid)';
    default:
      return 'var(--vn-border-strong)';
  }
}

export function scheduleTypeLabel(type: string | undefined): string {
  switch (type) {
    case 'live':
      return 'Live';
    case 'ads':
      return 'Ad';
    case 'vod':
      return 'VOD';
    default:
      return type ? String(type) : '—';
  }
}

// ---------------------------------------------------------------------------
// Date / duration
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD` for the `date` query param. */
export function ymd(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/** Backend timestamp string, e.g. "2026-06-06 10:00:00 +07:00". */
export function formatScheduleStamp(d: dayjs.Dayjs): string {
  return d.format('YYYY-MM-DD HH:mm:ss Z');
}

/** Seconds → `HH:MM:SS` clock. */
export function formatDurationClock(totalSeconds: number | undefined): string {
  const s = Math.max(0, Math.floor(totalSeconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/** Seconds → `M:SS` (or `H:MM:SS` past an hour). Compact clock for table cells. */
export function formatDurationShort(totalSeconds: number | undefined): string {
  const s = Math.max(0, Math.floor(totalSeconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
}

/** Seconds → compact human duration, e.g. "1h 23m" / "45s". */
export function formatDurationHuman(totalSeconds: number | undefined): string {
  const s = Math.max(0, Math.floor(totalSeconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/** Epoch ms → `HH:mm`. */
export function clockFromMs(ms: number | undefined): string {
  if (!ms) return '—';
  return dayjs(ms).format('HH:mm');
}

/** Epoch ms → `MMM D, HH:mm:ss` — full date+time for cross-referencing air times. */
export function stampFromMs(ms: number | undefined): string {
  if (!ms) return '—';
  return dayjs(ms).format('MMM D, HH:mm:ss');
}

/** Label for the day-navigator ("Today", "Tomorrow", or a date). */
export function dayLabel(date: Date): string {
  const d = dayjs(date).startOf('day');
  const today = dayjs().startOf('day');
  const diff = d.diff(today, 'day');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.format('ddd, MMM D, YYYY');
}

export function isToday(date: Date): boolean {
  return dayjs(date).isSame(dayjs(), 'day');
}
